import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend
} from 'recharts';
import {
  TrendingUp, AlertTriangle, BarChart3, Plus, Edit, Trash2
} from 'lucide-react';

import { pickTimeBadge, picksHrBadge, perfBg, perfColor } from '@/utils/styleHelpers';

export const KPIOverviewTab = ({
  categoryChartData, performanceDistribution, analytics,
  filteredKpis, statusFilter, statusFilterLabel, categories,
  isGuest, isAdmin, isMasterAdmin,
  inlineEdit, setInlineEdit, handleInlineSave,
  setEditingKPI, handleDeleteKPI, setDialogOpen,
  getTrendIcon, getPerformanceColor, getPerformanceBg
}) => (
  <div className="space-y-6">
    <div className="grid lg:grid-cols-2 gap-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Performance by Category</CardTitle>
          <CardDescription>Average achievement vs target by category</CardDescription>
        </CardHeader>
        <CardContent>
          {categoryChartData.length > 0 ? (
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={categoryChartData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                  <XAxis type="number" domain={[0, 120]} tick={{ fontSize: 12 }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={100} />
                  <Tooltip 
                    formatter={(value) => [`${value}%`, 'Performance']}
                    contentStyle={{ borderRadius: '4px', border: '1px solid #e4e4e7' }}
                  />
                  <Bar dataKey="performance" radius={[0, 4, 4, 0]}>
                    {categoryChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-muted-foreground">No data available</div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Performance Distribution</CardTitle>
          <CardDescription>KPIs grouped by target achievement</CardDescription>
        </CardHeader>
        <CardContent>
          {performanceDistribution.length > 0 ? (
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={performanceDistribution} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={5} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                    {performanceDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-muted-foreground">No data available</div>
          )}
        </CardContent>
      </Card>
    </div>

    <div className="grid lg:grid-cols-2 gap-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-emerald-500" /> Top Performers
          </CardTitle>
        </CardHeader>
        <CardContent>
          {analytics?.top_performers?.length > 0 ? (
            <div className="space-y-3">
              {analytics.top_performers.map((kpi, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-emerald-50 rounded-sm border border-emerald-200">
                  <div><p className="font-medium">{kpi.name}</p><p className="text-sm text-muted-foreground capitalize">{kpi.category}</p></div>
                  <Badge className="bg-emerald-600">{kpi.performance}%</Badge>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">No data yet</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-rose-500" /> Needs Attention
          </CardTitle>
        </CardHeader>
        <CardContent>
          {analytics?.needs_attention?.length > 0 ? (
            <div className="space-y-3">
              {analytics.needs_attention.map((kpi, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-rose-50 rounded-sm border border-rose-200">
                  <div><p className="font-medium">{kpi.name}</p><p className="text-sm text-muted-foreground capitalize">{kpi.category}</p></div>
                  <Badge variant="destructive">{kpi.performance}%</Badge>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">All KPIs are on track!</p>
          )}
        </CardContent>
      </Card>
    </div>

    <Card data-testid="overview-kpi-table">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-blue-500" />
              {statusFilterLabel}
            </CardTitle>
            <CardDescription>
              {statusFilter === 'all' ? 'Click any KPI to edit its values directly' : `Showing ${filteredKpis.length} ${statusFilter === 'on_target' ? 'on target' : 'needs attention'} KPIs`}
            </CardDescription>
          </div>
          <Badge variant="outline" className="text-xs" data-testid="kpi-count-badge">{filteredKpis.length} total</Badge>
        </div>
      </CardHeader>
      <CardContent>
        {filteredKpis.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="kpi-overview-table">
              <thead>
                <tr className="border-b border-muted text-left">
                  <th className="pb-3 pr-4 font-medium text-muted-foreground">KPI Name</th>
                  <th className="pb-3 pr-4 font-medium text-muted-foreground">Category</th>
                  <th className="pb-3 pr-4 font-medium text-muted-foreground text-right">Value</th>
                  <th className="pb-3 pr-4 font-medium text-muted-foreground text-right">Target</th>
                  <th className="pb-3 pr-4 font-medium text-muted-foreground text-right">Performance</th>
                  <th className="pb-3 pr-4 font-medium text-muted-foreground text-center">Trend</th>
                  {!isGuest && <th className="pb-3 font-medium text-muted-foreground text-center">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {filteredKpis.map((kpi) => {
                  const perf = kpi.target > 0 ? Math.round((kpi.value / kpi.target) * 100) : 0;
                  const perfBadgeCls = perfBg(perf);
                  return (
                    <tr key={kpi.metric_id} className={`border-b border-muted/50 transition-colors hover:bg-muted/30 ${!isGuest ? 'cursor-pointer' : ''}`}
                      onClick={() => !isGuest && setEditingKPI({...kpi, category: (kpi.category || '').toLowerCase()})}
                      data-testid={`overview-kpi-row-${kpi.metric_id}`}>
                      <td className="py-3 pr-4">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{kpi.name}</span>
                          {isGuest && <Badge variant="outline" className="text-[10px] px-1 py-0 border-sky-300 text-sky-600 bg-sky-50">Sample</Badge>}
                        </div>
                      </td>
                      <td className="py-3 pr-4"><Badge variant="outline" className="capitalize text-xs" style={{ borderColor: categories.find(c => c.value === kpi.category)?.color }}>{kpi.category}</Badge></td>
                      <td className="py-3 pr-4 text-right font-mono font-semibold" onClick={(e) => e.stopPropagation()}>
                        {!isGuest && isAdmin && inlineEdit?.metricId === kpi.metric_id && inlineEdit?.field === 'value' ? (
                          <Input type="number" autoFocus defaultValue={kpi.value} className="h-7 w-20 text-right text-sm font-mono ml-auto"
                            onBlur={(e) => handleInlineSave(kpi.metric_id, 'value', e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleInlineSave(kpi.metric_id, 'value', e.target.value); if (e.key === 'Escape') setInlineEdit(null); }}
                            data-testid={`inline-value-${kpi.metric_id}`} />
                        ) : (
                          <span className={!isGuest && isAdmin ? 'cursor-text hover:bg-blue-50 hover:outline hover:outline-1 hover:outline-blue-300 px-1.5 py-0.5 rounded transition-colors' : ''}
                            onClick={() => !isGuest && isAdmin && setInlineEdit({ metricId: kpi.metric_id, field: 'value' })}
                            data-testid={`value-cell-${kpi.metric_id}`}>{kpi.value}{kpi.unit}</span>
                        )}
                      </td>
                      <td className="py-3 pr-4 text-right font-mono text-muted-foreground" onClick={(e) => e.stopPropagation()}>
                        {!isGuest && isAdmin && inlineEdit?.metricId === kpi.metric_id && inlineEdit?.field === 'target' ? (
                          <Input type="number" autoFocus defaultValue={kpi.target} className="h-7 w-20 text-right text-sm font-mono ml-auto"
                            onBlur={(e) => handleInlineSave(kpi.metric_id, 'target', e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleInlineSave(kpi.metric_id, 'target', e.target.value); if (e.key === 'Escape') setInlineEdit(null); }}
                            data-testid={`inline-target-${kpi.metric_id}`} />
                        ) : (
                          <span className={!isGuest && isAdmin ? 'cursor-text hover:bg-blue-50 hover:outline hover:outline-1 hover:outline-blue-300 px-1.5 py-0.5 rounded transition-colors' : ''}
                            onClick={() => !isGuest && isAdmin && setInlineEdit({ metricId: kpi.metric_id, field: 'target' })}
                            data-testid={`target-cell-${kpi.metric_id}`}>{kpi.target}{kpi.unit}</span>
                        )}
                      </td>
                      <td className="py-3 pr-4 text-right">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${perfBadgeCls}`}>{perf}%</span>
                      </td>
                      <td className="py-3 pr-4 text-center">{getTrendIcon(kpi.trend)}</td>
                      {!isGuest && (
                        <td className="py-3 text-center">
                          <div className="flex items-center justify-center gap-1" onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="icon" className="h-7 w-7"
                              onClick={() => setEditingKPI({...kpi, category: (kpi.category || '').toLowerCase()})}
                              data-testid={`overview-edit-kpi-${kpi.metric_id}`}><Edit className="h-3.5 w-3.5" /></Button>
                            {isMasterAdmin && (
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-500 hover:text-rose-700"
                                onClick={() => handleDeleteKPI(kpi.metric_id)}
                                data-testid={`overview-delete-kpi-${kpi.metric_id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8">
            <BarChart3 className="h-10 w-10 mx-auto mb-3 text-muted-foreground opacity-50" />
            <p className="text-muted-foreground">
              {statusFilter === 'all' ? 'No KPIs found' : `No ${statusFilter === 'on_target' ? 'on target' : 'needs attention'} KPIs`}
            </p>
            {!isGuest && statusFilter === 'all' && (
              <Button variant="outline" className="mt-3" onClick={() => setDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" /> Add your first KPI
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  </div>
);
